export const MAX_WORLD_MISSIONS = 3;

export function worldPins(value: unknown): string[] {
  return Array.isArray(value) ? [...new Set(value.filter((id): id is string => typeof id === 'string').map(id => id.trim()).filter(Boolean))].slice(0, MAX_WORLD_MISSIONS) : [];
}

export function toggleWorldPin(pins: string[], id: string): string[] {
  const current = worldPins(pins);
  return current.includes(id) ? current.filter(pin => pin !== id) : worldPins([...current, id]);
}

export type LandmarkStage = 'foundation' | 'building' | 'complete';
export function landmarkStage(achieved: boolean, completed: number, percent?: number): LandmarkStage {
  return achieved ? 'complete' : completed > 0 || (Number.isFinite(percent) && percent! > 0) ? 'building' : 'foundation';
}

export interface GroundPoint { x: number; z: number }
export interface GroundObstacle extends GroundPoint { radius: number }
export const WORLD_WALK_LIMIT = 60;

function clearSegment(a: GroundPoint, b: GroundPoint, obstacles: GroundObstacle[]): boolean {
  const dx = b.x - a.x, dz = b.z - a.z, length = dx * dx + dz * dz;
  return obstacles.every(obstacle => {
    const t = length ? Math.max(0, Math.min(1, ((obstacle.x - a.x) * dx + (obstacle.z - a.z) * dz) / length)) : 0;
    return Math.hypot(a.x + t * dx - obstacle.x, a.z + t * dz - obstacle.z) >= obstacle.radius;
  });
}

/** A small visibility graph routes the Guide around work areas, never through furniture. */
export function worldWalkPath(start: GroundPoint, destination: GroundPoint, obstacles: GroundObstacle[]): GroundPoint[] {
  if (![start.x, start.z, destination.x, destination.z].every(Number.isFinite)) return [];
  const target = { ...destination };
  const distance = Math.hypot(target.x, target.z);
  if (distance > WORLD_WALK_LIMIT) { target.x *= WORLD_WALK_LIMIT / distance; target.z *= WORLD_WALK_LIMIT / distance; }
  for (let pass = 0; pass < 4; pass++) for (const obstacle of obstacles) {
    const dx = target.x - obstacle.x, dz = target.z - obstacle.z, length = Math.hypot(dx, dz);
    if (length < obstacle.radius + 0.2) {
      target.x = obstacle.x + (length ? dx / length : 1) * (obstacle.radius + 0.2);
      target.z = obstacle.z + (length ? dz / length : 0) * (obstacle.radius + 0.2);
    }
  }
  if (Math.hypot(target.x, target.z) > WORLD_WALK_LIMIT || obstacles.some(obstacle => Math.hypot(target.x - obstacle.x, target.z - obstacle.z) < obstacle.radius)) return [];
  // Newly added landmarks may surround the current position; let the Guide step out.
  const blockers = obstacles.filter(obstacle => Math.hypot(start.x - obstacle.x, start.z - obstacle.z) >= obstacle.radius);
  if (clearSegment(start, target, blockers)) return [target];
  const nodes = [start, target, ...blockers.flatMap(obstacle => Array.from({ length: 12 }, (_, i) => ({
    x: obstacle.x + Math.cos(i * Math.PI / 6) * (obstacle.radius + 0.4),
    z: obstacle.z + Math.sin(i * Math.PI / 6) * (obstacle.radius + 0.4),
  }))).filter(point => Math.hypot(point.x, point.z) <= WORLD_WALK_LIMIT && blockers.every(obstacle => Math.hypot(point.x - obstacle.x, point.z - obstacle.z) >= obstacle.radius))];
  const distances = nodes.map(() => Infinity), previous = nodes.map(() => -1), visited = new Set<number>();
  distances[0] = 0;
  while (visited.size < nodes.length) {
    let nearest = -1;
    for (let i = 0; i < nodes.length; i++) if (!visited.has(i) && (nearest < 0 || distances[i] < distances[nearest])) nearest = i;
    if (nearest < 0 || !Number.isFinite(distances[nearest])) return [];
    if (nearest === 1) break;
    visited.add(nearest);
    for (let i = 0; i < nodes.length; i++) if (!visited.has(i)) {
      const distance = distances[nearest] + Math.hypot(nodes[i].x - nodes[nearest].x, nodes[i].z - nodes[nearest].z);
      if (distance < distances[i] && clearSegment(nodes[nearest], nodes[i], blockers)) { distances[i] = distance; previous[i] = nearest; }
    }
  }
  const path: GroundPoint[] = [];
  for (let i = 1; i > 0; i = previous[i]) path.unshift(nodes[i]);
  return path;
}
